import type { PublicResource } from '../lib/apiClient';

export type ResourceType = string;

export interface ResourceAuthor {
  kind: 'team' | 'guest';
  name: string;
  title: string;
  company?: string;
  initials: string;
  color: string;
  photoUrl?: string;
}

export interface ResourceItem {
  type: ResourceType;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  publishedAt: string;
  readTime?: string;
  duration?: string;
  videoUrl?: string;
  stat?: string;
  statLabel?: string;
  author: ResourceAuthor;
  body: unknown;
}

const AVATAR_COLORS = ['#8a4fa6', '#2670ad', '#1caa3c', '#d6a039', '#0b5c22', '#b8433a'];

export function normalizeResourceType(value: string): ResourceType {
  const slug = value.trim().toLowerCase().replace(/[\s_]+/g, '-');
  if (['article', 'articles'].includes(slug)) return 'article';
  if (['video', 'videos', 'video-interview', 'video-interviews'].includes(slug)) return 'video';
  if (['story', 'stories', 'company-story', 'company-stories'].includes(slug)) return 'story';
  return slug;
}

export function resourceVideoSource(value?: string): { src: string; embed: boolean } | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) return undefined;
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'youtu.be'].includes(host)) {
      const parts = url.pathname.split('/').filter(Boolean);
      const id = host === 'youtu.be' ? parts[0] : url.pathname === '/watch' ? url.searchParams.get('v') : ['embed', 'shorts', 'live'].includes(parts[0]) ? parts[1] : undefined;
      if (!id || !/^[\w-]+$/.test(id)) return undefined;
      return { src: `https://www.youtube-nocookie.com/embed/${id}`, embed: true };
    }
    if (['vimeo.com', 'player.vimeo.com'].includes(host)) {
      const match = url.pathname.match(/^\/(?:video\/)?(\d+)(?:\/([\w]+))?\/?$/);
      if (!match) return undefined;
      const embed = new URL(`https://player.vimeo.com/video/${match[1]}`);
      const hash = url.searchParams.get('h') || match[2];
      if (hash) embed.searchParams.set('h', hash);
      return { src: embed.href, embed: true };
    }
    return { src: url.href, embed: false };
  } catch {
    return undefined;
  }
}

function displayName(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'QH';
}

function avatarColor(seed: string): string {
  const hash = [...seed].reduce((value, character) => value + character.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function formatPublishedAt(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(date);
}

export function mapPublicResource(resource: PublicResource): ResourceItem {
  return {
    type: normalizeResourceType(resource.type),
    slug: resource.slug,
    category: displayName(resource.category),
    title: resource.title,
    excerpt: resource.excerpt?.trim() || '',
    publishedAt: formatPublishedAt(resource.publishedAt),
    readTime: resource.readTime ? `${resource.readTime} min read` : undefined,
    duration: resource.duration ? `${resource.duration} min` : undefined,
    videoUrl: resource.videoUrl?.trim() || undefined,
    stat: resource.stat?.trim() || undefined,
    statLabel: resource.statLabel?.trim() || undefined,
    author: {
      kind: resource.author.is_guest ? 'guest' : 'team',
      name: resource.author.name,
      title: resource.author.designation?.trim() || '',
      company: resource.author.company?.trim() || undefined,
      initials: initials(resource.author.name),
      color: avatarColor(resource.author.slug || resource.author.name),
      photoUrl: resource.author.photo_url?.trim() || undefined,
    },
    body: resource.body,
  };
}
